// Tree → zones. The only place that decides what a shape means: which nodes are
// one bag, which qualify their own terms, and which cannot be drawn at all.
//
// Nothing here mutates: a shape that does not fit reports a blocker and the host
// falls back to text.
import {
  Lexicon,
  QueryFilter,
  QueryGroup,
  ReferencedArtifact,
} from 'src/app/shared/models/ai-search.interface';
import {
  QbArtifactKind,
  QbArtifactRef,
  QbBlocker,
  QbBlockerKind,
  QbGroup,
  QbJoiner,
  QbModel,
  QbNode,
  QbTerm,
  artifactKind,
} from './query-builder.types';
import {
  isExclusionSubtree,
  isExclusionsNode,
  isLeaf,
  operatorOf,
  queryCounts,
} from './query-builder.tree';

// The banner reads `blockers[0].detail`; describeBlockers summarises the rest by
// kind. One table, so the two lines cannot describe the same shape differently.
const BLOCKER_DETAIL: Record<QbBlockerKind, string> = {
  'inert-exclusion': 'exclusions inside an OR group, which exclude nothing',
  'mixed-node': 'a group holding both included and excluded terms',
  'unsupported-operator': 'an operator the zones cannot draw',
  'scoped-negation': 'an exclusion that excludes nothing where it sits',
};

function blocker(
  kind: QbBlockerKind,
  path: number[],
  detail = BLOCKER_DETAIL[kind],
  terms?: string[],
): QbBlocker {
  return terms ? { kind, path, detail, terms } : { kind, path, detail };
}

/**
 * Negated terms in an OR bag match every article that does *not* contain them,
 * so the query matches almost everything — the exclusion has never excluded
 * anything. The old editor let people write this; it is a defect in the saved
 * query, not a shape to render, so it is called out by name.
 */
function inertOrMixed(
  operator: 'and' | 'or',
  path: number[],
  negated: QueryFilter[],
  rootLevel = false,
): QbBlocker {
  if (operator === 'or') {
    return blocker(
      'inert-exclusion',
      path,
      BLOCKER_DETAIL['inert-exclusion'],
      negated.map((f) => (f.name ?? '').trim() || f.value),
    );
  }
  return blocker(
    'mixed-node',
    path,
    rootLevel
      ? 'the query mixes included and excluded terms at its root'
      : BLOCKER_DETAIL['mixed-node'],
  );
}

// --- Derivation -------------------------------------------------------------

function volumeOf(lexicon: Lexicon | null, value: string): number | null {
  const hit = (lexicon?.verification ?? []).find((v) => v.branch === value);
  return hit && typeof hit.vol === 'number' ? hit.vol : null;
}

function termOf(
  filter: QueryFilter,
  path: number[],
  index: number,
  lexicon: Lexicon | null,
): QbTerm {
  const value = filter.value ?? '';
  return {
    // Fall back to the value so a filter stored without a name still reads.
    label: (filter.name ?? '').trim() || value,
    value,
    field: filter.field ?? 'text',
    negated: !!filter.negated,
    path,
    index,
    artifact: artifactKind(filter.field),
    volume: volumeOf(lexicon, value),
  };
}

export function termsOf(
  filters: QueryFilter[],
  path: number[],
  lexicon: Lexicon | null,
  keep: (f: QueryFilter) => boolean = () => true,
): QbTerm[] {
  return filters
    .map((filter, index) => ({ filter, index }))
    .filter(({ filter }) => keep(filter))
    .map(({ filter, index }) => termOf(filter, path, index, lexicon));
}

// 'all' only where the node really says AND over several terms — the wording in
// the UI is read from the operator, never assumed.
function joinerOf(operator: string, termCount: number): QbJoiner {
  return operator === 'and' && termCount > 1 ? 'all' : 'any';
}

// A group with no context and no guards: the root's own filters, and every
// ordinary OR bag.
function plainGroup(
  path: number[],
  terms: QbTerm[],
  joiner: QbJoiner,
  restructurable = false,
): QbGroup {
  return {
    joiner,
    path,
    terms,
    context: null,
    guard: null,
    // A plain bag owns nothing below it, so the joiner is inert here.
    subs: [],
    subJoiner: 'and',
    restructurable,
  };
}

/** A leaf child an AND node may own: nothing nested, nothing negated. */
function isQualifier(child: QbNode): boolean {
  return isLeaf(child) && !(child.filters ?? []).some((f) => f.negated);
}

/**
 * A node whose children qualify its own terms — a term with required context, or
 * one with scoped exclusions. It has to stay a single block: flattening it would
 * keep the meaning but lose the reason the terms are grouped at all.
 */
function qualifiesOwnTerms(node: QbNode): boolean {
  const children = node.groups ?? [];
  if (!children.length || !children.every((child) => isLeaf(child))) {
    return false;
  }
  // A node with no included filters of its own qualifies nothing: under an AND
  // joiner `and(or(a), or(b))` is two required bags, and chaining says so. Under
  // an OR joiner it cannot chain anyway, and classifyGroup reads it as one block.
  if (!(node.filters ?? []).some((f) => !f.negated)) return false;
  // Scoped exclusions qualify the terms whatever the operator; required context
  // only means that under AND.
  return children.some(isExclusionsNode) || operatorOf(node) === 'and';
}

function classifyGroup(
  node: QueryGroup,
  path: number[],
  blockers: QbBlocker[],
  lexicon: Lexicon | null,
): QbGroup | null {
  const op = operatorOf(node);
  if (!op) {
    blockers.push(
      blocker(
        'unsupported-operator',
        path,
        `“${node.operator}” has no place in the zones`,
      ),
    );
    return null;
  }

  const filters = node.filters ?? [];
  const included = filters.filter((f) => !f.negated);
  const negated = filters.filter((f) => f.negated);
  if (included.length && negated.length) {
    blockers.push(inertOrMixed(op, path, negated));
    return null;
  }
  if (!included.length && negated.length && !isLeaf(node)) {
    blockers.push(
      blocker(
        'scoped-negation',
        path,
        'an exclusion that scopes the terms nested under it',
      ),
    );
    return null;
  }

  // Split the children into the shapes a group may own: same-operator leaves
  // that are part of this very bag, the leaves an AND node qualifies its terms
  // with, and any number of guard leaves. Anything else is nesting we cannot draw.
  const children = node.groups ?? [];
  const guardIdx: number[] = [];
  const merged: number[] = [];
  const qualifying: number[] = [];
  children.forEach((child, i) => {
    if (isExclusionsNode(child)) {
      guardIdx.push(i);
      return;
    }
    // `or(or(a, b), or(c, d))` is one bag of four alternatives — saved queries
    // routinely split a bag across nodes like this. Same operator, no negation:
    // the terms belong to this group, each keeping its own path.
    if (operatorOf(child) === op && isQualifier(child)) {
      merged.push(i);
      return;
    }
    // Only an AND node can own qualifiers: a leaf child of an OR node is another
    // bag of alternatives, which is flattenable but not something to reshape here.
    if (op === 'and' && isQualifier(child)) {
      qualifying.push(i);
      return;
    }
    // Anything else is a group below a group: rule 7's "any other child is a
    // sub-lane". It used to be refused as deep nesting, which sent the whole
    // query to the structure view; it falls through to `subs` below instead, so
    // nothing has to be reshaped to stay editable.
  });

  // Where this group's own terms live. Normally the node's own filters — but the
  // generated entity block (`and(or(typed heads), or(context))`) has none, and
  // then the first qualifier supplies the terms and the second qualifies them.
  // Per the company_request contract that pair is one block, never loose chips.
  // A node with no included filters of its own takes its terms from the first
  // qualifier, whether or not a second one follows. With two, the second
  // qualifies the first (the entity block). With one — `and(or(name forms),
  // and(NOT …))`, the contract's wrapper — the qualifier IS the terms: reading
  // it as a context instead produced a block with an empty term row, which
  // `isIncomplete` then reported and the re-run gate refused.
  const ownsTerms = !!included.length || !!merged.length;
  const termsIdx =
    !ownsTerms && qualifying.length ? (qualifying.shift() as number) : -1;
  const contextIdx = qualifying.length ? (qualifying.shift() as number) : -1;
  // One qualifier is the context; anything past that falls through to `subs`.

  const termsNode = termsIdx >= 0 ? children[termsIdx] : node;
  const termsPath = termsIdx >= 0 ? [...path, termsIdx] : path;
  const context = contextIdx >= 0 ? children[contextIdx] : null;
  const contextPath = context ? [...path, contextIdx] : null;

  const terms =
    termsIdx >= 0
      ? termsOf(termsNode.filters ?? [], termsPath, lexicon)
      : [
          ...termsOf(filters, path, lexicon),
          ...merged.flatMap((i) =>
            termsOf(children[i].filters ?? [], [...path, i], lexicon),
          ),
        ];
  const contextTerms = context
    ? termsOf(context.filters ?? [], contextPath as number[], lexicon)
    : [];
  const guardTerms = guardIdx.flatMap((i) =>
    termsOf(children[i].filters ?? [], [...path, i], lexicon),
  );

  // Whatever no zone accounted for is a sub-lane, classified by these very rules
  // one level in — so a sub-lane's own context, guards and subs are read the
  // same way, and tree order falls out of the walk rather than a sort. A child
  // the zones still cannot draw returns null and keeps its own blocker.
  const consumed = new Set([...guardIdx, ...merged, termsIdx, contextIdx]);
  const subs = children
    .map((child, i) =>
      consumed.has(i)
        ? null
        : classifyGroup(child, [...path, i], blockers, lexicon),
    )
    .filter((group): group is QbGroup => !!group);

  return {
    // The terms are joined by the operator of the node holding them, which is
    // not `op` once they come from a child.
    joiner: joinerOf(operatorOf(termsNode) ?? op, terms.length),
    path: termsPath,
    terms,
    context: contextPath
      ? {
          path: contextPath,
          joiner: joinerOf(
            operatorOf(context as QueryGroup) ?? 'or',
            contextTerms.length,
          ),
          terms: contextTerms,
        }
      : null,
    // A guard is a bag of negated terms: any one of them rules the article out,
    // whatever operator joins them, so its joiner is never read from the node.
    guard: guardIdx.length
      ? { path: [...path, guardIdx[0]], joiner: 'any', terms: guardTerms }
      : null,
    subs,
    // How a sub joins this group: the owning node's operator. An OR-ed sub is an
    // alternative, an AND-ed one a further requirement — the panel says which.
    subJoiner: op,
    // Exactly what splitGroup/mergeGroups accept: one top-level node, nothing
    // nested under it, nothing negated.
    restructurable:
      path.length === 1 && !children.length && !filters.some((f) => f.negated),
  };
}

/**
 * The embedded artifacts, read straight off the root's own arrays — the only
 * place they live. No position is invented for them: the server appends each one
 * as another child of the root, joined by the root's own operator.
 */
function referencedArtifacts(tree: QbNode | null | undefined): QbArtifactRef[] {
  if (!tree || !('referenced_queries' in tree)) return [];
  const read = (
    list: ReferencedArtifact[] | undefined,
    kind: QbArtifactKind,
  ): QbArtifactRef[] =>
    (list ?? []).map((a, index) => ({
      kind,
      id: a.artifact_id,
      name: (a.name ?? '').trim() || a.artifact_id,
      negated: !!a.negated,
      index,
    }));
  return [
    ...read(tree.referenced_queries, 'query'),
    ...read(tree.referenced_portfolios, 'portfolio'),
  ];
}

// Flatten the tree into the zones. Nothing is reshaped: a shape that does not
// fit reports a blocker and the host shows the query as text.
export function deriveBuilderModel(
  input: QbNode | null | undefined,
  lexicon: Lexicon | null = null,
): QbModel {
  const blockers: QbBlocker[] = [];
  const groups: QbGroup[] = [];
  const exclusions: QbTerm[] = [];
  let exclusionsPath: number[] | null = null;
  // No query yet reads as an empty AND root, which the normal path already
  // renders as "no groups, nothing excluded".
  const tree: QbNode = input ?? { operator: 'and', filters: [], groups: [] };

  const rootOp = operatorOf(tree);
  if (!rootOp) {
    blockers.push(
      blocker(
        'unsupported-operator',
        [],
        `“${tree.operator}” has no place in the zones`,
      ),
    );
  }
  // Anything that is not an OR root behaves as AND, including a broken operator.
  const isAndRoot = rootOp !== 'or';
  const rootFilters = tree.filters ?? [];
  const rootChildren = tree.groups ?? [];
  const rootIncluded = rootFilters.filter((f) => !f.negated);
  const rootNegated = rootFilters.filter((f) => f.negated);

  const orExcludesNothing = (path: number[]): void => {
    blockers.push(
      blocker(
        'scoped-negation',
        path,
        'an exclusion OR-ed into the query excludes nothing',
      ),
    );
  };

  // The query-wide exclusions: negated filters on the root itself, then each
  // all-negated child. Under an OR root they exclude nothing, so they are a
  // blocker rather than a zone.
  if (rootIncluded.length && rootNegated.length) {
    blockers.push(
      inertOrMixed(isAndRoot ? 'and' : 'or', [], rootNegated, true),
    );
  } else if (rootNegated.length) {
    if (!isAndRoot) orExcludesNothing([]);
    else {
      exclusionsPath = [];
      exclusions.push(...termsOf(rootFilters, [], lexicon));
    }
  }

  // An all-negated node excludes the whole query under AND, and excludes nothing
  // under OR. Same rule wherever it turns up — at the root, or nested inside the
  // AND wrapper a saved query puts its exclusion groups in.
  const takeExclusions = (
    node: QbNode,
    path: number[],
    andSide: boolean,
  ): void => {
    if (!andSide) {
      orExcludesNothing(path);
      return;
    }
    // Recursive: the terms can sit one level down, inside the AND wrapper.
    const collect = (from: QbNode, at: number[]): void => {
      if ((from.filters ?? []).length && !exclusionsPath) exclusionsPath = at;
      exclusions.push(...termsOf(from.filters ?? [], at, lexicon));
      (from.groups ?? []).forEach((child, i) => collect(child, [...at, i]));
    };
    collect(node, path);
  };

  const peeled = new Set<number>();
  rootChildren.forEach((child, i) => {
    // A whole exclusion subtree, not just a leaf: a saved query keeps its
    // negated bags inside an AND node, so the exclusions arrive as grandchildren.
    if (!isExclusionSubtree(child)) return;
    peeled.add(i);
    takeExclusions(child, [i], isAndRoot);
  });

  // Whatever is left after the exclusions are peeled off is the query's content.
  // `and(X, NOT y)` is how an exclusion gets added to a query whose groups are
  // alternatives, so that wrapper is stepped through: otherwise the
  // alternatives inside X would read as nesting and the whole query would fall
  // back to text the moment it gained its first exclusion.
  const contentIdx = rootChildren
    .map((_, i) => i)
    .filter((i) => !peeled.has(i));
  const wrapper =
    isAndRoot && !rootIncluded.length && contentIdx.length === 1
      ? contentIdx[0]
      : null;
  const wrapped = wrapper != null ? rootChildren[wrapper] : null;
  const unwrap =
    !!wrapped &&
    operatorOf(wrapped) === 'or' &&
    !(wrapped.filters ?? []).some((f) => f.negated) &&
    // An empty node is not a wrapper around anything. Stepping through it hid
    // the group a user had just added: the click looked like it did nothing, and
    // the second one produced two.
    !!((wrapped.filters ?? []).length || (wrapped.groups ?? []).length);

  // One pair — the node the zones are read from, and the path that addresses
  // it — plus the children to classify, resolved here so the loop below needs
  // no index bookkeeping.
  const content: { node: QbNode; path: number[] } =
    unwrap && wrapped
      ? { node: wrapped, path: [wrapper as number] }
      : { node: tree, path: [] };
  const children =
    unwrap && wrapped
      ? (wrapped.groups ?? []).map((node, i) => ({
          node,
          path: [wrapper as number, i],
        }))
      : contentIdx.map((i) => ({ node: rootChildren[i], path: [i] }));

  // The content node's operator is the joiner by definition: unwrapping only
  // happens into an `or`, and a non-AND root is its own content node.
  const joiner: 'and' | 'or' = operatorOf(content.node) === 'or' ? 'or' : 'and';

  const ownFilters = content.node.filters ?? [];
  const ownIncluded = ownFilters.filter((f) => !f.negated);
  if (ownIncluded.length) {
    // The content node's own filters are a group like any other — under an AND
    // root they are already required, so `split` only has to move them.
    groups.push(
      plainGroup(
        content.path,
        termsOf(ownFilters, content.path, lexicon, (f) => !f.negated),
        joinerOf(joiner, ownIncluded.length),
        // Only the root's own filters can be split — under an unwrapped root the
        // mutator would read the path as a child index.
        !content.path.length,
      ),
    );
  }

  // `or(a, or(b, c))` means `or(a, b, c)` — same-operator nesting is
  // associativity, and generated queries chain their alternatives that way
  // (identifiers, then legal names, then a guarded short form). Walk the chain
  // instead of refusing to draw it; each node keeps its own path, so editing a
  // term still writes exactly where it came from.
  const queue = [...children];
  while (queue.length) {
    const { node, path } = queue.shift() as {
      node: QueryGroup;
      path: number[];
    };
    if (isExclusionsNode(node)) {
      takeExclusions(node, path, joiner === 'and');
      continue;
    }
    const own = node.filters ?? [];
    const chains =
      operatorOf(node) === joiner &&
      !!(node.groups ?? []).length &&
      !own.some((f) => f.negated) &&
      !qualifiesOwnTerms(node);
    if (chains) {
      if (own.length) {
        groups.push(
          plainGroup(
            path,
            termsOf(own, path, lexicon),
            joinerOf(joiner, own.length),
          ),
        );
      }
      // Onto the front, so the blocks read in the order the tree writes them:
      // breadth-first put a chained node's own alternatives after every sibling
      // branch, which reads as shuffled once a query has more than one level.
      queue.unshift(
        ...(node.groups ?? []).map((child, i) => ({
          node: child,
          path: [...path, i],
        })),
      );
      continue;
    }
    const group = classifyGroup(node, path, blockers, lexicon);
    if (group) groups.push(group);
  }

  const refs = referencedArtifacts(input);

  return {
    joiner,
    rootJoiner: isAndRoot ? 'and' : 'or',
    contentPath: content.path,
    groups,
    artifacts: refs.filter((a) => !a.negated),
    excludedArtifacts: refs.filter((a) => a.negated),
    exclusions,
    exclusionsPath,
    counts: queryCounts(tree),
    blockers,
    representable: blockers.length === 0,
  };
}

// One line for the read-only banner: what stops the zones, in the user's terms.
/** `1 term` / `3 terms` — the only two places that count anything for the UI. */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function describeBlockers(blockers: QbBlocker[]): string {
  if (!blockers.length) return '';
  const parts: string[] = [];
  const count = (kind: QbBlockerKind): number =>
    blockers.filter((b) => b.kind === kind).length;

  const inert = blockers.filter((b) => b.kind === 'inert-exclusion');
  if (inert.length) {
    const terms = inert.reduce((n, b) => n + (b.terms?.length ?? 0), 0);
    parts.push(
      plural(
        terms,
        'exclusion that excludes nothing',
        'exclusions that exclude nothing',
      ),
    );
  }
  const mixed = count('mixed-node');
  if (mixed) parts.push(plural(mixed, 'mixed node', 'mixed nodes'));
  const scoped = count('scoped-negation');
  if (scoped) parts.push(plural(scoped, 'OR-ed exclusion', 'OR-ed exclusions'));
  const operators = blockers.filter((b) => b.kind === 'unsupported-operator');
  if (operators.length) {
    parts.push(
      plural(operators.length, 'unsupported operator', 'unsupported operators'),
    );
  }
  return parts.join(', ');
}

// --- What the UI may offer --------------------------------------------------

/**
 * Why split is unavailable here, in the user's terms — empty when it is
 * available. The menu shows the item either way: a control that comes and goes
 * reads as arbitrary, while a disabled one with a reason teaches the shape.
 */
export function splitUnavailable(model: QbModel, index: number): string {
  const group = model.groups[index];
  if (!group) return 'there is no group here';
  if (model.joiner !== 'and') {
    return 'the groups here are alternatives, so splitting one would change what the query means';
  }
  if (!group.restructurable) {
    return 'these terms are stored across more than one group';
  }
  if (group.terms.length < 2)
    return 'a group needs more than one term to split';
  if (group.terms.some((t) => t.negated))
    return 'this group holds an exclusion';
  return '';
}

export function mergeUnavailable(
  model: QbModel,
  index: number,
  direction: 'up' | 'down',
): string {
  const other = index + (direction === 'up' ? -1 : 1);
  if (other < 0 || other >= model.groups.length) {
    return direction === 'up'
      ? 'this is the first group'
      : 'this is the last group';
  }
  if (model.joiner !== 'and') {
    return 'the groups here are already alternatives';
  }
  if (
    !model.groups[index]?.restructurable ||
    !model.groups[other]?.restructurable
  ) {
    return 'these terms are stored across more than one group';
  }
  return '';
}

/**
 * Why the joiner *between* blocks cannot be changed — empty when it can. Only
 * one block is the one real reason: `setTopJoiner` keeps the exclusions AND-ed
 * at the root by restructuring, so they are no longer in the way.
 */
export function topJoinerUnavailable(model: QbModel): string {
  if (model.groups.length < 2) {
    return 'there is only one block, so there is nothing to combine';
  }
  return '';
}

/**
 * Whether a group's `any`/`all` can be changed. Not for the content node's own
 * filters — that node's operator is the joiner *between* groups, so changing it
 * there would silently rewrite the whole query's shape.
 */
export function canSetJoiner(model: QbModel, index: number): boolean {
  const group = model.groups[index];
  return (
    !!group &&
    !!group.path.length &&
    !group.context &&
    group.terms.length > 1 &&
    group.terms.every((t) => !t.negated)
  );
}

export function canSplit(model: QbModel, index: number): boolean {
  return !splitUnavailable(model, index);
}

export function canMerge(
  model: QbModel,
  index: number,
  direction: 'up' | 'down',
): boolean {
  return !mergeUnavailable(model, index, direction);
}

/** The terms `hoistInertExclusions` would move, for the copy that offers it. */
export function inertExclusions(model: QbModel): string[] {
  return model.blockers
    .filter((b) => b.kind === 'inert-exclusion')
    .flatMap((b) => b.terms ?? []);
}
